import React from 'react';
import { EnhancedInject } from '../types/exercise';

interface ValidationBadgeProps {
  inject: EnhancedInject;
  onFix?: () => void;
}

// Client-side validation functions (simplified versions)
function validatePrincipleAdherence(inject: EnhancedInject): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const { principleKey, dilemmas, expectedActions, injectText, teachingPoint } = inject;
  
  switch(principleKey) {
    case "SEE_THINK_ACT_REPEAT": // OODA
      const hasSpeedCertainty = dilemmas.some((d: string) => 
        /speed|certainty|quick|wait|immediate|delay|fast|slow|rush|pause/i.test(d)
      );
      const hasOODASteps = expectedActions.some((a: string) => 
        /observe|orient|decide|act|ooda/i.test(a)
      );
      if (!hasSpeedCertainty) errors.push("Missing speed vs certainty dilemma");
      if (!hasOODASteps) errors.push("Missing OODA sequence");
      break;
      
    case "STAKEHOLDER_BALANCE":
      const hasStakeholderTradeoffs = dilemmas.some((d: string) => 
        /(people|patient|staff|customer).*(board|executive|reputation|service)|(board|executive|reputation|service).*(people|patient|staff|customer)/i.test(d)
      );
      if (!hasStakeholderTradeoffs) errors.push("Missing stakeholder trade-offs");
      break;
      
    case "SITREP":
      const hasSITREPStructure = expectedActions.some((a: string) => 
        /at time|at place|what happened|what done|what next/i.test(a)
      ) || teachingPoint.toLowerCase().includes("sitrep");
      if (!hasSITREPStructure) errors.push("Missing SITREP structure");
      break;
      
    // Add other principles as needed
  }
  
  return { valid: errors.length === 0, errors };
}

function validateOperationalActions(inject: EnhancedInject): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const { expectedActions } = inject;
  
  const rolePattern = /\b(Site Lead|Comms Lead|Clinical Lead|Safety Officer|Recorder|Board Liaison|IT Lead|HR Lead|Operations Lead|Security|Reception Lead)\b/i;
  const timeboxPattern = /\b\d+\s*(sec|second|min|minute|hr|hour)s?\b/i;
  const channelPattern = /\b(000|111|999|911|112|Teams|radio|phone|email|PA|SMS|Signal|Slack|secure line|CCTV|IVR|chat|intranet|call|via)\b/i;
  
  expectedActions.forEach((action: string, index: number) => {
    if (!rolePattern.test(action)) {
      errors.push(`Action ${index + 1} missing role`);
    }
    if (!timeboxPattern.test(action)) {
      errors.push(`Action ${index + 1} missing timebox`);
    }
    if (!channelPattern.test(action)) {
      errors.push(`Action ${index + 1} missing channel`);
    }
  });
  
  return { valid: errors.length === 0, errors };
}

export const ValidationBadge: React.FC<ValidationBadgeProps> = ({ inject, onFix }) => {
  const principleValidation = validatePrincipleAdherence(inject);
  const actionValidation = validateOperationalActions(inject);
  const isValid = principleValidation.valid && actionValidation.valid;
  
  const allErrors = [...principleValidation.errors, ...actionValidation.errors];
  
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: isValid ? '#dcfce7' : '#fecaca',
      color: isValid ? '#166534' : '#dc2626',
      border: isValid ? '1px solid #bbf7d0' : '1px solid #fca5a5',
      gap: '4px'
    }}>
      {isValid ? '✓ Valid' : '⚠ Issues'}
      {!isValid && allErrors.length > 0 && (
        <span 
          style={{ 
            fontSize: '10px',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
          title={allErrors.join('; ')}
        >
          ({allErrors.length})
        </span>
      )}
      {!isValid && onFix && (
        <button 
          onClick={onFix}
          style={{ 
            marginLeft: '4px', 
            fontSize: '10px',
            padding: '2px 4px',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '2px',
            cursor: 'pointer'
          }}
        >
          Fix
        </button>
      )}
    </div>
  );
};

